import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  signal,
  WritableSignal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import { ELEARNIO_ACTIVITIES } from '@core/constants/elearnio-activities.constant';
import { CourseService } from '@services/course.service';
import { CourseActivity } from '@models/course/course-activity.model';
import { CourseActivitySection } from '@models/course/course-activity-section.model';

import { LabelOverflowDirective } from './label-overflow.directive';

const ITEM_KINDS = {
  LABEL: 'label',
  MODULE: 'module',
} as const;

const MODULE_TYPE_KEY_PREFIX = 'h5p.libraries.';
const PANEL_BODY_ID = 'course-navigation-panel-body';

interface ModuleItemViewModel {
  kind: typeof ITEM_KINDS.MODULE;
  id: string;
  title: string;
  typeKey: string;
}

interface LabelItemViewModel {
  kind: typeof ITEM_KINDS.LABEL;
  id: string;
  text: string;
  textExpanded: WritableSignal<boolean>;
  overflowing: WritableSignal<boolean>;
}

type ItemViewModel = ModuleItemViewModel | LabelItemViewModel;

interface SectionViewModel {
  id: string;
  title: string;
  items: ItemViewModel[];
}

@Component({
  selector: 'app-course-navigation-panel',
  templateUrl: './course-navigation-panel.component.html',
  styleUrls: ['./course-navigation-panel.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, TranslateModule, LabelOverflowDirective],
})
export class CourseNavigationPanelComponent {
  private readonly courseService = inject(CourseService);

  readonly collapsed = model(false);
  readonly sections = input<CourseActivitySection[]>([]);
  readonly panelBodyId = PANEL_BODY_ID;
  readonly ITEM_KINDS = ITEM_KINDS;

  private readonly labelTextExpandedState = new Map<
    string,
    WritableSignal<boolean>
  >();
  private readonly labelOverflowingState = new Map<
    string,
    WritableSignal<boolean>
  >();

  readonly viewModel = computed<SectionViewModel[]>(() => {
    const sortedSections = this.courseService.sortByOrder(
      this.sections() ?? [],
    );
    return sortedSections.map(section => ({
      id: section.id,
      title: section.title,
      items: this.courseService
        .sortByOrder(section.courseActivities ?? [])
        .map(a => this.buildItem(a)),
    }));
  });

  toggle(): void {
    this.collapsed.update(v => !v);
  }

  toggleLabelText(id: string): void {
    this.labelTextExpandedState.get(id)?.update(v => !v);
  }

  setLabelOverflowing(id: string, overflowing: boolean): void {
    const state = this.labelOverflowingState.get(id);
    if (state && state() !== overflowing) {
      state.set(overflowing);
    }
  }

  onLabelActivate(item: LabelItemViewModel): void {
    if (!item.overflowing()) {
      return;
    }
    this.toggleLabelText(item.id);
  }

  onLabelKeydown(item: LabelItemViewModel, event: Event): void {
    if (!item.overflowing()) {
      return;
    }
    event.preventDefault();
    this.toggleLabelText(item.id);
  }

  private buildItem(activity: CourseActivity): ItemViewModel {
    if (this.isLabel(activity)) {
      return this.buildLabelItem(activity);
    }
    return this.buildModuleItem(activity);
  }

  private buildLabelItem(activity: CourseActivity): LabelItemViewModel {
    return {
      kind: ITEM_KINDS.LABEL,
      id: activity.id,
      text: this.getLabelText(activity),
      textExpanded: this.getOrCreateSignal(
        this.labelTextExpandedState,
        activity.id,
      ),
      overflowing: this.getOrCreateSignal(
        this.labelOverflowingState,
        activity.id,
      ),
    };
  }

  private buildModuleItem(activity: CourseActivity): ModuleItemViewModel {
    return {
      kind: ITEM_KINDS.MODULE,
      id: activity.id,
      title: activity.title,
      typeKey: this.getTypeKey(activity),
    };
  }

  private getOrCreateSignal(
    map: Map<string, WritableSignal<boolean>>,
    id: string,
  ): WritableSignal<boolean> {
    let s = map.get(id);
    if (!s) {
      s = signal(false);
      map.set(id, s);
    }
    return s;
  }

  private isLabel(activity: CourseActivity): boolean {
    return activity?.h5pLibrary?.toLowerCase() === ELEARNIO_ACTIVITIES.LABEL;
  }

  private getLabelText(activity: CourseActivity): string {
    try {
      return JSON.parse(activity?.contentJSON ?? '')?.labelText ?? '';
    } catch {
      return '';
    }
  }

  private getTypeKey(activity: CourseActivity): string {
    const libraryId = (activity?.h5pLibrary ?? '').split(' ')[0];
    return libraryId ? `${MODULE_TYPE_KEY_PREFIX}${libraryId}` : '';
  }
}
